/**
 * Laguna Beach Run Club - Animation System
 * Scroll-triggered animations and interactive effects
 */

// Check for reduced motion preference
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Intersection Observer for scroll animations
class ScrollAnimations {
  constructor() {
    this.observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1,
    };

    this.init();
  }

  init() {
    if (prefersReducedMotion) {
      // Immediately show all animated elements
      document.querySelectorAll('[data-animate], [data-animate-children]').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), this.observerOptions);

    // Observe single elements
    document.querySelectorAll('[data-animate]').forEach((el) => {
      this.observer.observe(el);
    });

    // Observe parent containers for staggered children
    document.querySelectorAll('[data-animate-children]').forEach((el) => {
      this.observer.observe(el);
    });
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');

        // Unobserve unless repeat is enabled
        if (!entry.target.dataset.animateRepeat) {
          this.observer.unobserve(entry.target);
        }
      }
    });
  }
}

// Parallax effect for images
class ParallaxEffect {
  constructor() {
    this.elements = document.querySelectorAll('[data-parallax]');
    this.ticking = false;

    if (this.elements.length && !prefersReducedMotion) {
      this.init();
    }
  }

  init() {
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    this.update();
  }

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    this.elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const speed = parseFloat(el.dataset.parallax) || 0.5;

      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const yPos = (rect.top - window.innerHeight / 2) * speed;
        el.style.transform = `translateY(${yPos}px)`;
      }
    });
  }
}

// Magnetic button effect
class MagneticButtons {
  constructor() {
    this.buttons = document.querySelectorAll('.btn-magnetic');

    if (!prefersReducedMotion) {
      this.init();
    }
  }

  init() {
    this.buttons.forEach((btn) => {
      btn.addEventListener('mousemove', this.handleMouseMove.bind(this));
      btn.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    });
  }

  handleMouseMove(e) {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const strength = 0.3;

    btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
  }

  handleMouseLeave(e) {
    e.currentTarget.style.transform = 'translate(0, 0)';
  }
}

// Split text animation
class SplitText {
  constructor() {
    this.elements = document.querySelectorAll('[data-split-text]');
    this.init();
  }

  init() {
    this.elements.forEach((el) => {
      const text = el.textContent;
      el.innerHTML = '';

      text.split(' ').forEach((word, wordIndex) => {
        const wordSpan = document.createElement('span');
        wordSpan.className = 'word';

        word.split('').forEach((char, charIndex) => {
          const charSpan = document.createElement('span');
          charSpan.className = 'char';
          charSpan.textContent = char;
          charSpan.style.transitionDelay = `${(wordIndex * 5 + charIndex) * 30}ms`;
          wordSpan.appendChild(charSpan);
        });

        el.appendChild(wordSpan);
        el.appendChild(document.createTextNode(' '));
      });
    });
  }
}

// Counter animation
class CounterAnimation {
  constructor() {
    this.counters = document.querySelectorAll('[data-counter]');
    this.init();
  }

  init() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    this.counters.forEach((counter) => observer.observe(counter));
  }

  animateCounter(el) {
    const target = parseInt(el.dataset.counter, 10);
    const duration = parseInt(el.dataset.counterDuration, 10) || 2000;
    const start = 0;
    const startTime = performance.now();

    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const current = Math.floor(start + (target - start) * easedProgress);

      el.textContent = current.toLocaleString();

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }
}

// Smooth scroll for anchor links
class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();

          const headerHeight = document.querySelector('[data-header]')?.offsetHeight || 0;
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        }
      });
    });
  }
}

// Image reveal on load
class ImageReveal {
  constructor() {
    this.images = document.querySelectorAll('[data-image-reveal]');
    this.init();
  }

  init() {
    this.images.forEach((img) => {
      if (img.complete) {
        img.parentElement?.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => {
          img.parentElement?.classList.add('is-loaded');
        });
      }
    });
  }
}

// Initialize all animations
document.addEventListener('DOMContentLoaded', () => {
  new ScrollAnimations();
  new ParallaxEffect();
  new MagneticButtons();
  new SplitText();
  new CounterAnimation();
  new SmoothScroll();
  new ImageReveal();
});

// Reinitialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', () => {
  new ScrollAnimations();
});
